import React, { useEffect, useState } from 'react';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { useBunkerContext } from 'src/context/BunkerContext';
import { BunkeringEvent } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { isBunkeringOnboardAtDate } from '../../BunkeringUtils';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { AssignDebunkeringButtonWrapper, BunkerRegInputGrid, BunkerRegInputRow } from '../../Bunkering.styles';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { TabLineButton } from 'src/styles/button.style';
import { useAuthStore } from 'src/context/AuthContext';
import { UUID } from 'crypto';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { mapBunkering } from 'src/factory/Bunkering/BunkeringFactory';

interface DebunkeringAssignmentProps {
    debunkeringEvent: BunkeringEvent;
}

const DebunkeringAssignment: React.FC<DebunkeringAssignmentProps> = ({ debunkeringEvent }) => {
    const { bunkerings, setSelectedBunkering, setSelectedBunkeringEventId, setSelectedBunkeringEvent } = useBunkerContext();
    const { role, loggedInUser } = useAuthStore();
    const [selectedBunkeringRef, setSelectedBunkeringRef] = useState<Bunkering>(null);
    const { upsertBunkering } = useGetBunkeringData();

    useEffect(() => {
        if (bunkerings && bunkerings.length > 0) {
            const possibleBunkerings = bunkerings.filter(
                (b) => b.fuelType === debunkeringEvent.fuelType && isBunkeringOnboardAtDate(b, debunkeringEvent.bunkeringDateTimeLocal)
            );
            if (possibleBunkerings.length === 1) {
                setSelectedBunkeringRef(possibleBunkerings[0]);
            }
        } else {
            setSelectedBunkeringRef(null);
        }
    }, [bunkerings, debunkeringEvent]);

    const handleBunkeringReferenceChange = (changeObject: InputChangeData) => {
        const selectedBunkeringReference = changeObject.event.target.value;
        const selectedBunkering = bunkerings.find((b) => b.bunkeringReference === selectedBunkeringReference);
        if (selectedBunkering) {
            setSelectedBunkeringRef(selectedBunkering);
        } else {
            setSelectedBunkeringRef(null);
        }
    };

    const handleAssignDebunkering = () => {
        if (selectedBunkeringRef) {
            selectedBunkeringRef.debunkeringEventIds.push(debunkeringEvent.eventID as UUID);

            // update the bunkering
            selectedBunkeringRef.lastUpdatedAt = new Date();
            selectedBunkeringRef.lastUpdatedBy = loggedInUser.userName ?? loggedInUser.appAccessToken;
            //selectedBunkeringRef.bunkeringStatuses.push(debunkeredStatus);
            const bunkeringBM = mapBunkering(selectedBunkeringRef);

            upsertBunkering(selectedBunkeringRef.imoNumber, bunkeringBM);

            // set selected bunkering event to the bunkering which was assigned the debunkering
            setSelectedBunkering(selectedBunkeringRef);
            setSelectedBunkeringEventId(selectedBunkeringRef.bunkeringEvent.id);
            setSelectedBunkeringEvent(selectedBunkeringRef.bunkeringEvent);
        }
    };

    return (
        <div>
            <BunkerRegInputGrid>
                <BunkerRegInputRow>
                    Debunkering date/time:
                    <div>{prettifyDateTimeHHmm(debunkeringEvent.bunkeringDateTimeLocal)}</div>
                </BunkerRegInputRow>
                <BunkerRegInputRow>
                    Port call:
                    <div>{`${debunkeringEvent.voyageNumber} ${debunkeringEvent.portName}`}</div>
                </BunkerRegInputRow>
            </BunkerRegInputGrid>
            <OdfjellInputComponent
                description="Select which fuel was debunkered: "
                type="select"
                options={bunkerings
                    .filter((b) => b.fuelType === debunkeringEvent.fuelType && isBunkeringOnboardAtDate(b, debunkeringEvent.bunkeringDateTimeLocal))
                    .map((b) => b.bunkeringReference)}
                value={selectedBunkeringRef?.bunkeringReference}
                selectDefaultOption="Select bunkering reference"
                onChange={handleBunkeringReferenceChange}
            />
            {selectedBunkeringRef !== null && (
                <BunkerRegInputGrid>
                    <BunkerRegInputRow>
                        Bunkering date/time:
                        <div>{prettifyDateTimeHHmm(selectedBunkeringRef.bunkeringDateTime)}</div>
                    </BunkerRegInputRow>
                    <BunkerRegInputRow>
                        Port call:
                        <div>{`${selectedBunkeringRef.bunkeringEvent?.voyageNumber} ${selectedBunkeringRef.bunkeringEvent?.portName}`}</div>
                    </BunkerRegInputRow>
                </BunkerRegInputGrid>
            )}
            {role?.isWriter && (
                <AssignDebunkeringButtonWrapper>
                    <TabLineButton type="button" onClick={() => handleAssignDebunkering()} disabled={selectedBunkeringRef === null}>
                        Confirm
                    </TabLineButton>
                </AssignDebunkeringButtonWrapper>
            )}
        </div>
    );
};

export default DebunkeringAssignment;
